import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getStats } from '../lib/api';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import CoachingModal from '../components/CoachingModal';
import MobileBackButton from '../components/MobileBackButton';
import { 
  LayoutDashboard, 
  Users, 
  Settings, 
  FileText, 
  TrendingUp,
  Calendar,
  Bell,
  LogOut,
  Menu,
  X,
  ChevronRight,
  FlaskConical,
  ClipboardList,
  Mail,
  MailCheck,
  Send,
  Loader2,
  CheckCircle2,
  BookOpen,
  ShoppingCart,
  BarChart3,
  Bug,
  FolderOpen,
  Sparkles,
  TestTube2,
  Smile
} from 'lucide-react';

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { path: '/admin/experiment', label: 'Salgs-Eksperiment', icon: TestTube2 },
    { path: '/admin/satisfaction', label: 'Kundetilfredshed', icon: Smile },
    { path: '/admin/email-flow', label: 'Email Follow-up', icon: MailCheck },
    { path: '/admin/test', label: 'Test & Opsætning', icon: FlaskConical },
    { path: '/admin/users', label: 'Medarbejdere', icon: Users },
    { path: '/admin/survey', label: 'Spørgeskema', icon: ClipboardList },
    { path: '/admin/survey-log', label: 'Survey Log', icon: Send },
    { path: '/admin/email', label: 'Email-skabelon', icon: Mail },
    { path: '/admin/tactics', label: 'Taktik-bibliotek', icon: BookOpen },
    { path: '/admin/categories', label: 'Produktkategorier', icon: FolderOpen },
    { path: '/admin/knowledge', label: 'Spørg Gardinbussen', icon: Sparkles },
    { path: '/admin/debug', label: 'Debug', icon: Bug },
    { path: '/admin/hubhus', label: 'Hubhus Config', icon: Settings },
    { path: '/admin/logs', label: 'Sync Logs', icon: FileText },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    return location.pathname.startsWith(item.path);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - with safe area for iOS PWA */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-100
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
        flex flex-col safe-top
      `}>
        <div className="flex flex-col h-full overflow-hidden">
          {/* Logo */}
          <div className="p-6 border-b border-slate-100">
            <Link to="/admin" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold font-outfit">GB</span>
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-900 font-outfit">Gardinbussen Intra</h1>
                <p className="text-xs text-slate-400">Admin Panel</p>
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-xl transition-colors
                    ${active 
                      ? 'bg-slate-900 text-white' 
                      : 'text-slate-600 hover:bg-slate-100'
                    }
                  `}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                  {active && <ChevronRight className="h-4 w-4 ml-auto" />}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-slate-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                <span className="text-slate-600 font-medium">
                  {user?.name?.charAt(0) || 'A'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="w-full justify-start"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Log ud
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen">
        {/* Mobile header - with safe area for iOS PWA */}
        <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-slate-100 p-4 safe-top">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <Menu className="h-6 w-6 text-slate-600" />
              </button>
              <MobileBackButton className="ml-1" />
            </div>
            <span className="font-bold text-slate-900 font-outfit">Gardinbussen Intra</span>
            <button
              onClick={handleLogout}
              className="p-2 hover:bg-slate-100 rounded-lg"
            >
              <LogOut className="h-5 w-5 text-slate-600" />
            </button>
          </div>
        </header>

        {/* Page content */}
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ label, value, icon: Icon, color, onClick }) => (
  <Card 
    className={`border-slate-100 hover:shadow-md transition-shadow ${onClick ? 'cursor-pointer' : ''}`}
    onClick={onClick}
  >
    <CardContent className="p-6">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-slate-500">{label}</span>
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <p className="text-3xl font-bold text-slate-900 font-outfit">{value}</p>
      {onClick && (
        <p className="text-xs text-slate-400 mt-2">Klik for detaljer</p>
      )}
    </CardContent>
  </Card>
);

// Daily Stats Row Component
const DailyStatsRow = ({ day }) => (
  <tr className="border-b border-slate-50 hover:bg-slate-50">
    <td className="py-3 px-2">
      <span className="font-medium text-slate-900">{day.day_name}</span>
      <span className="text-slate-400 text-sm ml-2">{day.date}</span>
    </td>
    <td className="text-center py-3 px-2">
      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${day.surveys_answered > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
        {day.surveys_answered}
      </span>
    </td>
    <td className="text-center py-3 px-2">
      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${day.prep_views > 0 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-400'}`}>
        {day.prep_views}
      </span>
    </td>
    <td className="text-center py-3 px-2">
      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${day.sms_sent > 0 ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-400'}`}>
        {day.sms_sent}
      </span>
    </td>
  </tr>
);

// Consultant Stats Row Component
const ConsultantStatsRow = ({ consultant }) => {
  const openRate = consultant.coaching_generated > 0 
    ? Math.round((consultant.prep_views / consultant.coaching_generated) * 100) 
    : 0;
  
  let rateColor = 'bg-slate-100 text-slate-400';
  if (openRate >= 80) rateColor = 'bg-emerald-100 text-emerald-700';
  else if (openRate >= 50) rateColor = 'bg-amber-100 text-amber-700';
  else if (openRate > 0) rateColor = 'bg-red-100 text-red-700';
  
  return (
    <tr className="border-b border-slate-50 hover:bg-slate-50">
      <td className="py-3 px-2 font-medium text-slate-900">{consultant.consultant_name}</td>
      <td className="text-center py-3 px-2 text-slate-600">{consultant.total_bookings}</td>
      <td className="text-center py-3 px-2 text-slate-600">{consultant.coaching_generated}</td>
      <td className="text-center py-3 px-2">
        <span className={`inline-flex items-center justify-center px-2 py-1 rounded-full text-sm ${consultant.prep_views > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
          {consultant.prep_views}
        </span>
      </td>
      <td className="text-center py-3 px-2">
        <span className={`inline-flex items-center justify-center px-2 py-1 rounded-full text-sm font-medium ${rateColor}`}>
          {openRate}%
        </span>
      </td>
    </tr>
  );
};

// Dashboard Page
export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [detailedStats, setDetailedStats] = useState(null);
  const [conversionStats, setConversionStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingConversion, setLoadingConversion] = useState(false);
  const [syncingMagento, setSyncingMagento] = useState(false);
  const [showSurveysModal, setShowSurveysModal] = useState(false);
  const [showCoachingModal, setShowCoachingModal] = useState(false);
  const [answeredSurveys, setAnsweredSurveys] = useState([]);
  const [loadingSurveys, setLoadingSurveys] = useState(false);
  const [selectedCoaching, setSelectedCoaching] = useState(null);
  const [loadingCoaching, setLoadingCoaching] = useState(false);

  useEffect(() => {
    loadStats();
    loadConversionStats();
  }, []);

  const loadStats = async () => {
    try {
      const basicRes = await getStats();
      setStats(basicRes.data);
      
      const token = localStorage.getItem('auth_token');
      const detailedRes = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/stats/detailed`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDetailedStats(detailedRes.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAnsweredSurveys = async () => {
    setLoadingSurveys(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/surveys/answered`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAnsweredSurveys(res.data.surveys || []);
    } catch (error) {
      console.error('Failed to load answered surveys:', error);
    } finally {
      setLoadingSurveys(false);
    }
  };

  const loadCoaching = async (bookingId) => {
    setLoadingCoaching(true);
    setShowCoachingModal(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/coaching/${bookingId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedCoaching(res.data);
    } catch (error) {
      console.error('Failed to load coaching:', error);
      setSelectedCoaching(null);
    } finally {
      setLoadingCoaching(false);
    }
  };

  const handleSurveysClick = () => {
    setShowSurveysModal(true);
    loadAnsweredSurveys();
  };

  const loadConversionStats = async () => {
    setLoadingConversion(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/magento/conversion-stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConversionStats(res.data);
    } catch (error) {
      console.error('Failed to load conversion stats:', error);
    } finally {
      setLoadingConversion(false);
    }
  };

  const handleSyncMagento = async () => {
    setSyncingMagento(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/magento/sync-now`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Wait a bit then reload stats
      setTimeout(() => {
        loadConversionStats();
        setSyncingMagento(false);
      }, 5000);
    } catch (error) {
      console.error('Failed to sync Magento:', error);
      setSyncingMagento(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('da-DK', { 
        day: 'numeric', 
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl">
        <div className="mb-8">
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      </div>
    );
  }

  const surveysSent = detailedStats?.survey_stats?.sent ?? '-';
  const surveysAnswered = detailedStats?.survey_stats?.answered ?? '-';
  const responseRate = detailedStats?.survey_stats?.response_rate 
    ? `${detailedStats.survey_stats.response_rate}%` 
    : '-';
  const totalBookings = stats?.total_bookings ?? '-';
  const totalUsers = stats?.total_users ?? '-';
  const coachingDelivered = stats?.coaching_delivered ?? '-';
  
  // Coaching stats
  const coachingTotal = detailedStats?.coaching_stats?.total ?? 0;
  const coachingFromSurvey = detailedStats?.coaching_stats?.from_survey ?? 0;
  const coachingFallback = detailedStats?.coaching_stats?.fallback ?? 0;

  const dailyStats = detailedStats?.daily_stats || [];
  const consultantStats = detailedStats?.consultant_stats || [];

  return (
    <div className="max-w-6xl">
      {/* Surveys Modal */}
      <Dialog open={showSurveysModal} onOpenChange={setShowSurveysModal}>
        <DialogContent className="max-w-5xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              Besvarede surveys ({answeredSurveys.length})
            </DialogTitle>
          </DialogHeader>
          
          {loadingSurveys ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="overflow-auto flex-1">
              <table className="w-full">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-3 text-sm font-medium text-slate-500">Kunde</th>
                    <th className="text-left py-3 px-3 text-sm font-medium text-slate-500">Konsulent</th>
                    <th className="text-left py-3 px-3 text-sm font-medium text-slate-500">Besvaret</th>
                    <th className="text-left py-3 px-3 text-sm font-medium text-slate-500">Besøgstid</th>
                    <th className="text-center py-3 px-3 text-sm font-medium text-slate-500">Coaching</th>
                  </tr>
                </thead>
                <tbody>
                  {answeredSurveys.map((survey, idx) => (
                    <tr key={idx} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="py-3 px-3">
                        <p className="font-medium text-slate-900">{survey.customer_name}</p>
                        <p className="text-xs text-slate-400">{survey.address || '-'}</p>
                      </td>
                      <td className="py-3 px-3">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          {survey.consultant_name}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-sm text-slate-600">{formatDate(survey.submitted_at)}</td>
                      <td className="py-3 px-3 text-sm text-slate-600">{formatDate(survey.scheduled_start)}</td>
                      <td className="py-3 px-3 text-center">
                        {survey.has_coaching ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => loadCoaching(survey.booking_id)}
                          >
                            <Bell className="h-3 w-3 mr-1" />
                            Vis tips
                          </Button>
                        ) : (
                          <span className="text-xs text-slate-400">Ingen</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {answeredSurveys.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-slate-400">
                        Ingen besvarede surveys endnu
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Coaching Modal */}
      <CoachingModal 
        open={showCoachingModal} 
        onOpenChange={setShowCoachingModal} 
        coaching={selectedCoaching} 
        loading={loadingCoaching} 
      />

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 font-outfit">Dashboard</h1>
        <p className="text-slate-500 mt-1">Overblik over systemets aktivitet</p>
      </div>

      <div className="space-y-8">
        {/* Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <StatCard label="Surveys udsendt" value={surveysSent} icon={Mail} color="text-blue-600 bg-blue-50" />
          <StatCard 
            label="Surveys besvaret" 
            value={surveysAnswered} 
            icon={CheckCircle2} 
            color="text-emerald-600 bg-emerald-50"
            onClick={handleSurveysClick}
          />
          <StatCard label="Svarprocent" value={responseRate} icon={TrendingUp} color="text-purple-600 bg-purple-50" />
          <StatCard label="Total bookinger" value={totalBookings} icon={Calendar} color="text-slate-600 bg-slate-100" />
          <StatCard label="Aktive brugere" value={totalUsers} icon={Users} color="text-teal-600 bg-teal-50" />
          
          {/* Coaching Stats Card */}
          <Card className="border-slate-100 hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 mb-1">Coaching genereret</p>
                  <p className="text-3xl font-bold text-slate-900 font-outfit">{coachingTotal}</p>
                  <div className="mt-2 flex gap-3 text-xs">
                    <span className="inline-flex items-center px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      {coachingFromSurvey} fra survey
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full bg-slate-100 text-slate-600">
                      {coachingFallback} standard
                    </span>
                  </div>
                </div>
                <div className="p-3 rounded-xl bg-amber-50">
                  <Bell className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Conversion Stats Card */}
        {conversionStats && (
          <Card className="border-slate-100 bg-gradient-to-r from-emerald-50 to-blue-50">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ShoppingCart className="h-5 w-5 text-emerald-600" />
                    Konvertering til køb (Magento)
                  </CardTitle>
                  <p className="text-sm text-slate-500">
                    Ordrer oprettet inden {conversionStats.conversion_window_days} dage efter opmåling
                  </p>
                </div>
                <div className="text-right">
                  {conversionStats.updated_at && (
                    <p className="text-xs text-slate-400 mb-1">
                      Opdateret: {new Date(conversionStats.updated_at).toLocaleDateString('da-DK', { 
                        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' 
                      })}
                    </p>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleSyncMagento}
                    disabled={syncingMagento}
                    className="text-xs"
                  >
                    {syncingMagento ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        Synkroniserer...
                      </>
                    ) : (
                      'Synkroniser nu'
                    )}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* With Survey */}
                <div className="bg-white rounded-lg p-4 border border-emerald-200">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    <span className="font-medium text-slate-900">Med survey-svar</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-2xl font-bold text-emerald-600">{conversionStats.with_survey.rate}%</p>
                      <p className="text-xs text-slate-500">Konverteringsrate</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{conversionStats.with_survey.converted}</p>
                      <p className="text-xs text-slate-500">af {conversionStats.with_survey.total} kunder</p>
                    </div>
                  </div>
                  {conversionStats.with_survey.total_value > 0 && (
                    <p className="text-sm text-emerald-600 mt-2">
                      Total værdi: {conversionStats.with_survey.total_value.toLocaleString('da-DK')} kr
                    </p>
                  )}
                </div>
                
                {/* Without Survey */}
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-3">
                    <X className="h-5 w-5 text-slate-400" />
                    <span className="font-medium text-slate-900">Uden survey-svar</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-2xl font-bold text-slate-600">{conversionStats.without_survey.rate}%</p>
                      <p className="text-xs text-slate-500">Konverteringsrate</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{conversionStats.without_survey.converted}</p>
                      <p className="text-xs text-slate-500">af {conversionStats.without_survey.total} kunder</p>
                    </div>
                  </div>
                  {conversionStats.without_survey.total_value > 0 && (
                    <p className="text-sm text-slate-500 mt-2">
                      Total værdi: {conversionStats.without_survey.total_value.toLocaleString('da-DK')} kr
                    </p>
                  )}
                </div>
              </div>
              
              {/* Comparison */}
              {conversionStats.with_survey.total > 0 && conversionStats.without_survey.total > 0 && (
                <div className="mt-4 p-3 bg-white rounded-lg border border-slate-200">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-slate-500" />
                    <span className="text-sm text-slate-600">
                      {conversionStats.with_survey.rate > conversionStats.without_survey.rate ? (
                        <span className="text-emerald-600 font-medium">
                          +{(conversionStats.with_survey.rate - conversionStats.without_survey.rate).toFixed(1)} procentpoint
                        </span>
                      ) : conversionStats.with_survey.rate < conversionStats.without_survey.rate ? (
                        <span className="text-red-600 font-medium">
                          {(conversionStats.with_survey.rate - conversionStats.without_survey.rate).toFixed(1)} procentpoint
                        </span>
                      ) : (
                        <span className="text-slate-600">Samme konverteringsrate</span>
                      )}
                      {' '}forskel for kunder med survey-svar
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        
        {loadingConversion && !conversionStats && (
          <Card className="border-slate-100">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                <span className="text-slate-500">Henter konverteringsdata fra Magento...</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Daily Activity Chart */}
        {dailyStats.length > 0 && (
          <Card className="border-slate-100">
            <CardHeader>
              <CardTitle className="text-lg">Aktivitet de sidste 7 dage</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left py-3 px-2 text-sm font-medium text-slate-500">Dag</th>
                      <th className="text-center py-3 px-2 text-sm font-medium text-slate-500">Surveys besvaret</th>
                      <th className="text-center py-3 px-2 text-sm font-medium text-slate-500">Coaching åbnet</th>
                      <th className="text-center py-3 px-2 text-sm font-medium text-slate-500">SMS sendt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyStats.map((day, idx) => (
                      <DailyStatsRow key={idx} day={day} />
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Consultant Stats */}
        {consultantStats.length > 0 && (
          <Card className="border-slate-100">
            <CardHeader>
              <CardTitle className="text-lg">Coaching pr. medarbejder (sidste 14 dage)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left py-3 px-2 text-sm font-medium text-slate-500">Medarbejder</th>
                      <th className="text-center py-3 px-2 text-sm font-medium text-slate-500">Bookinger</th>
                      <th className="text-center py-3 px-2 text-sm font-medium text-slate-500">Coaching genereret</th>
                      <th className="text-center py-3 px-2 text-sm font-medium text-slate-500">Coaching åbnet</th>
                      <th className="text-center py-3 px-2 text-sm font-medium text-slate-500">Åbningsrate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consultantStats.map((consultant, idx) => (
                      <ConsultantStatsRow key={idx} consultant={consultant} />
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminLayout;
